import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { 
  formatDate, 
  formatNumber, 
  getStatusBadgeClass, 
  capitalize, 
  debounce,
  truncateText
} from '../utils/helpers';
import {
  Search,
  Filter,
  FileText,
  Video,
  Download,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  Bookmark,
  User,
  Clock,
  ExternalLink,
  Copy,
  AlertTriangle,
  ChevronDown,
  X,
  Link,
  Calendar,
  TrendingUp,
  Activity
} from 'lucide-react';

const Transcripts = () => {
  const [transcripts, setTranscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    sourceType: '',
    isBookmarked: '',
    source: '',
    mode: '', // single, bulk, or all (empty)
    dateRange: '30days' // Default to last 30 days
  });
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [bulkItemsModal, setBulkItemsModal] = useState({ isOpen: false, items: [], title: '' });
  const abortControllerRef = useRef(null);

  const fetchTranscripts = useCallback(async (page = 1) => {
    // Cancel any existing request after a small delay to prevent premature cancellation
    if (abortControllerRef.current) {
      // Give a small delay to prevent cancelling a fresh request immediately
      await new Promise(resolve => setTimeout(resolve, 50));
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      setLoading(true);
      
      // Build params object, only including non-empty values
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });
      
      // Only add filters if they have values
      if (filters.search && filters.search.trim()) {
        params.append('search', filters.search.trim());
      }
      if (filters.status && filters.status.trim()) {
        params.append('status', filters.status);
      }
      if (filters.sourceType && filters.sourceType.trim()) {
        params.append('sourceType', filters.sourceType);
      }
      if (filters.source && filters.source.trim()) {
        params.append('source', filters.source);
      }
      if (filters.isBookmarked && filters.isBookmarked.trim()) {
        params.append('isBookmarked', filters.isBookmarked);
      }
      if (filters.mode && filters.mode.trim()) {
        params.append('mode', filters.mode);
      }
      if (filters.dateRange && filters.dateRange !== 'all') {
        if (filters.dateRange === 'custom') {
          // Send custom date range
          if (customDateRange.startDate) {
            params.append('startDate', customDateRange.startDate);
          }
          if (customDateRange.endDate) {
            params.append('endDate', customDateRange.endDate);
          }
        } else {
          // Send predefined date range
          params.append('dateRange', filters.dateRange);
        }
      }

      // Increase timeout for search queries (especially for email searches)
      const timeoutDuration = filters.search && filters.search.includes('@') ? 180000 : 120000;
      
      const response = await api.get(`/admin/transcripts?${params}`, {
        signal: signal,
        timeout: timeoutDuration
      });
      
      // Only update state if request wasn't aborted
      if (!signal.aborted) {
        setTranscripts(response.data.transcripts);
        setPagination(response.data.pagination);
        // setStats(response.data.stats);
      }
    } catch (error) {
      // Don't show error if request was aborted
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED' || error.message?.includes('abort')) {
        console.log('Request aborted');
        return;
      }
      console.error('Fetch transcripts error:', error);
      if (!signal.aborted) {
        toast.error('Something went wrong! Tries again later.');
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [filters, customDateRange]);

  const fetchStats = useCallback(async () => {
    try {
      // Build params for stats filters
      const params = new URLSearchParams();
      
      // Add filters if they have values
      if (filters.status && filters.status.trim()) {
        params.append('status', filters.status);
      }
      if (filters.sourceType && filters.sourceType.trim()) {
        params.append('sourceType', filters.sourceType);
      }
      if (filters.source && filters.source.trim()) {
        params.append('source', filters.source);
      }
      if (filters.isBookmarked && filters.isBookmarked.trim()) {
        params.append('isBookmarked', filters.isBookmarked);
      }
      if (filters.mode && filters.mode.trim()) {
        params.append('mode', filters.mode);
      }
      if (filters.dateRange && filters.dateRange !== 'all') {
        if (filters.dateRange === 'custom') {
          if (customDateRange.startDate) {
            params.append('startDate', customDateRange.startDate);
          }
          if (customDateRange.endDate) {
            params.append('endDate', customDateRange.endDate);
          }
        } else {
          params.append('dateRange', filters.dateRange);
        }
      }
      if (filters.search && filters.search.trim()) {
        params.append('search', filters.search.trim());
      }
      
      const queryString = params.toString();
      const url = queryString ? `/admin/transcripts/stats?${queryString}` : '/admin/transcripts/stats';
      
      const response = await api.get(url);
      setStats(response.data);
    } catch (error) {
      console.error('Fetch stats error:', error);
      toast.error('Failed to load statistics');
    }
  }, [filters, customDateRange]);

  useEffect(() => {
    fetchTranscripts(currentPage);
    
    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchTranscripts, currentPage]);

  // Fetch stats separately on mount
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Debounced search with longer delay for better email search handling
  const debouncedSearch = debounce((searchTerm) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
    setCurrentPage(1); // Reset to first page on search
  }, 800); // Increased from 500ms to 800ms to reduce rapid requests

  const copyTranscript = async (transcript) => {
    try {
      let content = transcript.transcript;
      
      // If transcript content is null, try to get it from file path
      if (!content) {
        try {
          const response = await api.get(`/transcript/${transcript._id}/get-from-path`);
          content = response.data;
        } catch (pathError) {
          console.error('Error getting transcript from path:', pathError);
          toast.error('Transcript content unavailable');
          return;
        }
      }
      
      if (!content) {
        toast.error('Transcript content unavailable');
        return;
      }
      
      await navigator.clipboard.writeText(content);
      toast.success('Transcript copied to clipboard!');
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('Failed to copy transcript');
    }
  };

  const deleteTranscript = async (transcriptId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await api.delete(`/transcript/${transcriptId}`);
      toast.success('Transcript deleted successfully');
      fetchTranscripts(currentPage); // Refresh the list
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete transcript');
    }
  };

  const copyVideoUrl = async (sourceUrl) => {
    try {
      if (!sourceUrl) {
        toast.error('Video URL not available');
        return;
      }
      
      await navigator.clipboard.writeText(sourceUrl);
      toast.success('Video URL copied to clipboard!');
    } catch (error) {
      console.error('Copy URL error:', error);
      toast.error('Failed to copy video URL');
    }
  };

  const getSourceIcon = (sourceType) => {
    switch (sourceType) {
      case 'tiktok':
        return <Video className="w-4 h-4" />;
      case 'youtube':
        return <Video className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getSourceColor = (sourceType) => {
    switch (sourceType) {
      case 'tiktok':
        return 'text-pink-600 bg-pink-100';
      case 'youtube':
        return 'text-red-600 bg-red-100';
      case 'instagram':
        return 'text-purple-600 bg-purple-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const openBulkItemsModal = (bulkItems, title) => {
    setBulkItemsModal({
      isOpen: true,
      items: bulkItems || [],
      title: title
    });
  };

  const closeBulkItemsModal = () => {
    setBulkItemsModal({ isOpen: false, items: [], title: '' });
  };

  const getDateRangeText = () => {
    const now = new Date();
    let startDate;
    
    switch (filters.dateRange) {
      case 'today':
        return `Today (${now.toLocaleDateString()})`;
      case '7days':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 7);
        return `${startDate.toLocaleDateString()} - ${now.toLocaleDateString()}`;
      case '30days':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 30);
        return `${startDate.toLocaleDateString()} - ${now.toLocaleDateString()}`;
      case '90days':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 90);
        return `${startDate.toLocaleDateString()} - ${now.toLocaleDateString()}`;
      case 'year':
        return `Jan 1, ${now.getFullYear()} - ${now.toLocaleDateString()}`;
      case 'all':
        return 'All Time';
      case 'custom':
        if (customDateRange.startDate && customDateRange.endDate) {
          return `${new Date(customDateRange.startDate).toLocaleDateString()} - ${new Date(customDateRange.endDate).toLocaleDateString()}`;
        }
        return 'Select dates';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Transcripts</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage all user transcripts and processing status
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Showing: {getDateRangeText()}
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => {
              fetchTranscripts(currentPage);
              fetchStats();
            }}
            className="btn-secondary"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatNumber(stats.total || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatNumber(stats.completed || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Processing</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatNumber(stats.processing || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <FileText className="w-5 h-5 text-red-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Failed</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatNumber(stats.failed || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Bookmark className="w-5 h-5 text-purple-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Bookmarked</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatNumber(stats.bookmarked || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Date Range Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Today</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.today || 0)}
                </p>
              </div>
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Calendar className="w-5 h-5 text-indigo-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">This Week</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.thisWeek || 0)}
                </p>
              </div>
              <div className="p-2 bg-cyan-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-cyan-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.thisMonth || 0)}
                </p>
              </div>
              <div className="p-2 bg-teal-100 rounded-lg">
                <Activity className="w-5 h-5 text-teal-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">All Time</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.total || 0)}
                </p>
              </div>
              <div className="p-2 bg-gray-100 rounded-lg">
                <FileText className="w-5 h-5 text-gray-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search transcripts..."
                className="input pl-10"
                onChange={(e) => debouncedSearch(e.target.value)}
              />
            </div>
            
            <select
              className="input"
              value={filters.dateRange}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, dateRange: e.target.value }));
                if (e.target.value !== 'custom') {
                  setCustomDateRange({ startDate: '', endDate: '' });
                }
                setCurrentPage(1);
              }}
            >
              <option value="today">Today</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="year">This Year</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Range</option>
            </select>
            
            {filters.dateRange === 'custom' && (
              <>
                <input
                  type="date"
                  className="input"
                  value={customDateRange.startDate}
                  onChange={(e) => {
                    setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }));
                    setCurrentPage(1);
                  }}
                  placeholder="Start Date"
                  max={customDateRange.endDate || undefined}
                />
                <input
                  type="date"
                  className="input"
                  value={customDateRange.endDate}
                  onChange={(e) => {
                    setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }));
                    setCurrentPage(1);
                  }}
                  placeholder="End Date"
                  min={customDateRange.startDate || undefined}
                />
              </>
            )}
            
            <select
              className="input"
              value={filters.status}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, status: e.target.value }));
                setCurrentPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="complete">Complete</option>
              <option value="failed">Failed</option>
              <option value="unavailable">Unavailable</option>
            </select>
            
            <select
              className="input"
              value={filters.sourceType}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, sourceType: e.target.value }));
                setCurrentPage(1);
              }}
            >
              <option value="">All Platforms</option>
              <option value="tiktok">TikTok</option>
              <option value="youtube">YouTube</option>
              <option value="instagram">Instagram</option>
              <option value="other">Other</option>
            </select>

            <select
              className="input"
              value={filters.source}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, source: e.target.value }));
                setCurrentPage(1);
              }}
            >
              <option value="">All Sources</option>
              <option value="extension">Extension</option>
              <option value="web">Web</option>
            </select>
            
            <select
              className="input"
              value={filters.mode}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, mode: e.target.value }));
                setCurrentPage(1);
              }}
            >
              <option value="">All Modes</option>
              <option value="single">Single</option>
              <option value="bulk">Bulk</option>
            </select>
            <select
              className="input"
              value={filters.isBookmarked}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, isBookmarked: e.target.value }));
                setCurrentPage(1);
              }}
            >
              <option value="">All Items</option>
              <option value="true">Bookmarked</option>
              <option value="false">Not Bookmarked</option>
            </select>
          </div>
        </div>
      </div>

      {/* Transcripts Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="loading-spinner w-8 h-8"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>User</th>
                    <th>Platform</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Views</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transcripts.map((transcript) => (
                    <tr key={transcript._id} className="hover:bg-gray-50">
                      <td>
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            {transcript.thumbnail ? (
                              <img
                                src={transcript.thumbnail}
                                alt=""
                                className="w-12 h-12 rounded-lg object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                                <FileText className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <div className="text-sm font-medium text-gray-900">
                                {truncateText(transcript.title, 60)}
                              </div>
                              {transcript.isBulkEntry && transcript.bulkItems && transcript.bulkItems.length > 0 && (
                                <button
                                  onClick={() => openBulkItemsModal(transcript.bulkItems, transcript.title)}
                                  className="text-blue-600 hover:text-blue-800 transition-colors"
                                  title="View bulk items"
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                            {transcript.description && (
                              <div className="flex items-center space-x-2">
                                <div className="text-xs text-gray-500 mt-1">
                                  {truncateText(transcript.description, 80)}
                                </div>
                              </div>
                            )}
                            <div className="flex items-center mt-1 space-x-2">
                              {transcript.isBookmarked && (
                                <Bookmark className="w-3 h-3 text-yellow-500 fill-current" />
                              )}
                              {transcript.folder && (
                                <span className="text-xs text-gray-500">
                                  📁 {transcript.folder}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">
                            {transcript.userId?.name || 'Unknown'}
                          </div>
                          <div className="text-gray-500">
                            {transcript.userId?.email || 'N/A'}
                          </div>
                          <div className="text-gray-500">
                            Plan: {transcript.userId?.plan || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <div className={`p-1 rounded ${getSourceColor(transcript.sourceType)}`}>
                            {getSourceIcon(transcript.sourceType)}
                          </div>
                          <span className="text-sm font-medium">
                            {capitalize(transcript.sourceType)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium">
                            {capitalize(transcript.source)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(transcript.status)}`}>
                          {capitalize(transcript.status)}
                        </span>
                      </td>
                      <td>
                        <div className="text-xs text-gray-500">
                          {formatNumber(transcript.views || 0)}
                        </div>
                      </td>
                      <td>
                        <div className="text-sm text-gray-900">
                          {formatDate(transcript.createdAt)}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          {transcript.sourceUrl && (
                            <a
                              href={transcript.sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-400 hover:text-blue-600"
                              title="View Source"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          {transcript.sourceUrl && transcript.isBulkEntry !== true && (
                            <button
                              onClick={() => copyVideoUrl(transcript.sourceUrl)}
                              className="text-gray-400 hover:text-purple-600"
                              title="Copy Video URL"
                            >
                              <Link className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => copyTranscript(transcript)}
                            className="text-gray-400 hover:text-green-600"
                            title="Copy Transcript"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            className="text-gray-400 hover:text-gray-600"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteTranscript(transcript._id, transcript.title)}
                            className="text-gray-400 hover:text-red-600"
                            title="Delete Transcript"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {(pagination.hasPrevPage || pagination.hasNextPage) && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Page {pagination.currentPage || 1}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => prev - 1)}
              disabled={!pagination.hasPrevPage}
              className="btn-secondary disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={!pagination.hasNextPage}
              className="btn-secondary disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Bulk Items Modal */}
      {bulkItemsModal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={closeBulkItemsModal}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Bulk Items - {bulkItemsModal.title}
                  </h3>
                  <button
                    onClick={closeBulkItemsModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="mt-4 space-y-4 max-h-[70vh] overflow-y-auto">
                  {bulkItemsModal.items.map((item, index) => (
                    <div key={item._id || index} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start space-x-4">
                        {/* Thumbnail */}
                        <div className="flex-shrink-0">
                          {item.thumbnail ? (
                            <img
                              src={item.thumbnail}
                              alt=""
                              className="w-24 h-24 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center">
                              <FileText className="w-12 h-12 text-gray-400" />
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-gray-900 mb-1">
                                {item.title || 'Untitled'}
                              </h4>
                              
                              {/* Author Info */}
                              {item.author && (
                                <div className="flex items-center space-x-2 mb-2">
                                  <User className="w-3 h-3 text-gray-400" />
                                  <span className="text-xs text-gray-600">
                                    {item.author.username || item.author.displayName}
                                  </span>
                                </div>
                              )}

                              {/* Metadata */}
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className={`badge ${getStatusBadgeClass(item.status)}`}>
                                  {capitalize(item.status)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {capitalize(item.platform)}
                                </span>
                                {item.views && (
                                  <span className="text-xs text-gray-500">
                                    {formatNumber(item.views)} views
                                  </span>
                                )}
                                {item.duration && (
                                  <span className="text-xs text-gray-500">
                                    {item.duration}s
                                  </span>
                                )}
                                {item.createTime && (
                                  <span className="text-xs text-gray-500">
                                    {formatDate(new Date(item.createTime * 1000))}
                                  </span>
                                )}
                              </div>

                              {/* Error message */}
                              {item.error && (
                                <div className="flex items-start space-x-1 mt-2 text-xs text-red-600">
                                  <AlertTriangle className="w-3 h-3 mt-0.5" />
                                  <span>{item.error}</span>
                                </div>
                              )}

                              {/* Transcript preview */}
                              {item.transcript && (
                                <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-700 max-h-32 overflow-y-auto">
                                  {truncateText(item.transcript, 200)}
                                </div>
                              )}
                            </div>

                            {/* Actions */}
                            <div className="flex items-center space-x-2 ml-4">
                              {item.sourceUrl && (
                                <a
                                  href={item.sourceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-400 hover:text-blue-600"
                                  title="View Source"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              )}
                              {item.transcript && (
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(item.transcript);
                                    toast.success('Transcript copied!');
                                  }}
                                  className="text-gray-400 hover:text-green-600"
                                  title="Copy Transcript"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              )}
                               {item.sourceUrl && (
                            <button
                              onClick={() => copyVideoUrl(item.sourceUrl)}
                              className="text-gray-400 hover:text-purple-600"
                              title="Copy Video URL"
                            >
                              <Link className="w-4 h-4" />
                            </button>
                          )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {bulkItemsModal.items.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No bulk items found
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={closeBulkItemsModal}
                  className="btn-secondary w-full sm:w-auto"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transcripts;